import { useCallback, useMemo, useRef, useState } from 'react';
import { FlatList, LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, StyleSheet } from 'react-native';

import { CARD_GAP, FOCUS_RING_GUTTER } from '../utils/constants';
import { useCasinoLobbyContentWidth } from './use-casino-lobby-content-width';

const SCROLL_EPSILON = 1;
const SCROLL_EVENT_THROTTLE = 16;

export const useCarouselCardSize = (columns: number) => {
    const contentWidth = useCasinoLobbyContentWidth();

    return (contentWidth - CARD_GAP * (columns - 1)) / columns;
};

type UseDesktopCarouselParams = {
    step: number;
    initialCanScrollNext?: boolean;
};

export const useDesktopCarousel = <T>({ step, initialCanScrollNext = false }: UseDesktopCarouselParams) => {
    const listRef = useRef<FlatList<T>>(null);

    const scrollXRef = useRef(0);
    const contentSizeRef = useRef(0);
    const viewportSizeRef = useRef(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(initialCanScrollNext);

    const updateScrollState = useCallback(() => {
        const maxOffset = contentSizeRef.current - viewportSizeRef.current;
        setCanScrollPrev(scrollXRef.current > SCROLL_EPSILON);
        setCanScrollNext(scrollXRef.current < maxOffset - SCROLL_EPSILON);
    }, []);

    const onScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
            scrollXRef.current = contentOffset.x;
            contentSizeRef.current = contentSize.width;
            viewportSizeRef.current = layoutMeasurement.width;
            updateScrollState();
        },
        [updateScrollState]
    );

    const onContentSizeChange = useCallback(
        (width: number) => {
            contentSizeRef.current = width;
            updateScrollState();
        },
        [updateScrollState]
    );

    const onLayout = useCallback(
        (event: LayoutChangeEvent) => {
            viewportSizeRef.current = event.nativeEvent.layout.width;
            updateScrollState();
        },
        [updateScrollState]
    );

    const scrollByCards = useCallback(
        (delta: number) => {
            const maxOffset = Math.max(0, contentSizeRef.current - viewportSizeRef.current);
            const targetIndex = Math.round(scrollXRef.current / step) + delta;
            const offset = Math.min(Math.max(targetIndex * step, 0), maxOffset);
            listRef.current?.scrollToOffset({ offset, animated: true });
        },
        [step]
    );

    const scrollPrev = useCallback(() => scrollByCards(-1), [scrollByCards]);
    const scrollNext = useCallback(() => scrollByCards(1), [scrollByCards]);

    const listProps = useMemo(
        () => ({
            ref: listRef,
            onScroll,
            onContentSizeChange,
            onLayout,
            scrollEventThrottle: SCROLL_EVENT_THROTTLE,
            style: styles.list,
            contentContainerStyle: styles.listContent,
        }),
        [onScroll, onContentSizeChange, onLayout]
    );

    return { canScrollPrev, canScrollNext, scrollPrev, scrollNext, listProps };
};

const styles = StyleSheet.create({
    list: {
        margin: -FOCUS_RING_GUTTER,
    },
    listContent: {
        padding: FOCUS_RING_GUTTER,
    },
});
